interface ScheduledVisitSource {
    id: string;
    eventDate: string;
    eventTime?: string | null;
    outcome?: string | null;
}

interface ScheduledTodoSource {
    id: string;
    done: boolean;
    dueDate: string | null;
    dueDateTime: string | null;
}

export interface ScheduledVisitRow<TVisit> {
    key: string;
    when: Date;
    kind: "visit";
    awaitingOutcome: boolean;
    visit: TVisit;
}

export interface ScheduledTodoRow<TTodo> {
    key: string;
    when: Date | null;
    kind: "todo";
    todo: TTodo;
}

export type ScheduledActivityRow<TVisit, TTodo> =
    | ScheduledVisitRow<TVisit>
    | ScheduledTodoRow<TTodo>;

const todoDueMoment = (todo: ScheduledTodoSource): Date | null => {
    if (todo.dueDateTime) {
        return new Date(todo.dueDateTime);
    }
    if (todo.dueDate) {
        return new Date(`${todo.dueDate}T23:59`);
    }
    return null;
};

interface ScheduledActivityBounds {
    now: Date;
    keepCompletedIds: string[];
}

export const buildScheduledActivityRows = <
    TVisit extends ScheduledVisitSource,
    TTodo extends ScheduledTodoSource,
>(
    visits: TVisit[],
    todos: TTodo[],
    bounds: ScheduledActivityBounds,
): ScheduledActivityRow<TVisit, TTodo>[] => {
    const { now, keepCompletedIds } = bounds;
    const rows: ScheduledActivityRow<TVisit, TTodo>[] = [];

    visits
        .filter(v => !v.outcome?.trim())
        .forEach(v => {
            const when = new Date(`${v.eventDate}T${v.eventTime ?? "23:59"}`);
            rows.push({
                key: `visit-${v.id}`,
                when,
                kind: "visit",
                awaitingOutcome: when.getTime() < now.getTime(),
                visit: v,
            });
        });

    todos
        .filter(t => !t.done || keepCompletedIds.includes(t.id))
        .forEach(t => {
            rows.push({
                key: `todo-${t.id}`,
                when: todoDueMoment(t),
                kind: "todo",
                todo: t,
            });
        });

    return rows.sort(
        (a, b) =>
            (a.when?.getTime() ?? Number.POSITIVE_INFINITY) -
            (b.when?.getTime() ?? Number.POSITIVE_INFINITY),
    );
};

export const isScheduledTodoOverdue = (when: Date | null, now: Date): boolean => {
    return when !== null && when.getTime() < now.getTime();
};
